<template>
  <div class="branding-results">
    <div v-if="results.length > 0" class="branding-results__wrapper">
      <a
        v-for="(result, i) in results"
        :key="i"
        :href="result.links.directlink"
        class="branding-results__item"
      >
        <div class="branding-results__content">
          <h2>{{ result.title }}</h2>
          <span>{{ formatSubTitle(result.attributes) }}</span>
        </div>
        <div class="branding-results__action">
          <sanitas-button
            class="removeMargin"
            secondary
            @click="redirect(result.links)"
          >
            {{ $t('button-redirect') }}
          </sanitas-button>
        </div>
      </a>
    </div>
    <div v-else class="branding-results__wrapper">
      <span>{{ $t('no-results') }}</span>
    </div>
    <div
      v-if="results.length > 0 && total > limit"
      class="branding-results__loadMore"
    >
      <sanitas-button
        class="removeMargin"
        primary
        full-width-in-xs
        @click="limit += 5"
      >
        {{ $t('button-more') }}
      </sanitas-button>
    </div>
  </div>
</template>

<script>
import { provideI36n, useI36n } from '@jota-one/i36n/src/index.vue2'
import axios from 'axios'

export default {
  name: 'SanitasBrandingResults',
  components: {
    SanitasButton: () => {
      return import(
        /* webpackChunkName: "sanitas-components" */
        '@/components/sanitas-components/SanitasButton'
      )
    },
  },
  props: {
    language: {
      type: String,
      default: 'de',
    },
    serverUrl: {
      type: String,
      default: 'https://ohws.prospective.ch/public/v1/medium/1002048',
    },
  },
  setup(props) {
    const loadLabels = lang => import(`./i18n/${lang}.json`)
    provideI36n(props.language, { load: loadLabels })
    const { $label } = useI36n()

    return {
      $t: $label,
    }
  },
  data: () => ({
    offset: 0,
    results: [],
    limit: 5,
    total: 0,
    savedValues: {},
  }),
  watch: {
    limit() {
      this.loadData(this.savedValues)
    },
  },
  created() {
    window.addEventListener('hashchange', () => {
      this.readFromUrl()
    })
    this.readFromUrl()
  },
  methods: {
    redirect(links) {
      window.location.href = links.directlink
    },
    formatSubTitle(attrs) {
      return [50, 20, 30, 40].map(id => attrs[id][0]).join(', ')
    },
    loadData(values) {
      const { limit, offset, language, serverUrl } = this
      const params = Object.keys(values)
        .filter(el => el !== 'labelQuery')
        .map(key => {
          let value = values[key]
          if (Array.isArray(values[key])) {
            value = values[key].join(',')
          }

          return 'f=' + key + ':' + encodeURIComponent(value)
        })
        .join('&')
      const search = values.labelQuery ? `&q=${values.labelQuery}` : ''
      axios
        .get(
          `${serverUrl}/jobs?offset=${offset}&limit=${limit}&lang=${language}${search}&${params}`,
        )
        .then(({ data }) => {
          this.results = data.jobs
          this.total = data.total
        })
    },
    readFromUrl() {
      let values = {}
      let match
      const pl = /\+/g
      const search = /([^&=]+)=?([^&]*)/g
      const decode = s => decodeURIComponent(s.replace(pl, ' '))
      const query = window.location.hash.substring(1)
      while ((match = search.exec(query))) {
        const key = decode(match[1])
        const value = decode(match[2])
        if ([30, 20, 50].includes(Number(key))) {
          if (values[key]) {
            values[key].push(value)
          } else {
            values[key] = [value]
          }
        } else {
          values[key] = value
        }
      }

      this.savedValues = values
      this.loadData(this.savedValues)
    },
  },
}
</script>

<style scoped>
.branding-results {
  padding: 30px 40px;

  &__item {
    text-decoration: none;
    border-bottom: 0;
    padding: 20px 0;
    display: flex;
    justify-content: space-between;
    border-top: 1px solid var(--c-secondary-neutral-3);

    &:last-child {
      border-bottom: 1px solid var(--c-secondary-neutral-3);
    }
  }

  &__action {
    margin: auto 0 auto auto;

    .removeMargin {
      margin-bottom: 0 !important;
    }
  }

  &__loadMore {
    margin-top: 50px;
  }

  &__content {
    margin: auto auto auto 0;
    font-style: normal;
    font-weight: normal;
    max-width: 80%;

    h2 {
      font-size: 20px;
      font-weight: bold;
      line-height: 1.35;
      color: var(--c-primary-neutral-1);
    }

    span {
      font-size: 15px;
      line-height: 1.33;
      color: var(--c-primary-neutral-1);
    }
  }

  &__wrapper {
    width: 100%;
    margin: auto;
    max-width: 1000px;
  }
}

@media (max-width: 768px) {
  .branding-results {
    padding: 25px;

    &__loadMore {
      margin-top: 20px;
    }

    &__item {
      flex-wrap: wrap;
      padding-bottom: 25px;
    }

    &__content {
      max-width: initial;
      padding-bottom: 25px;
    }
  }
}
</style>
